import React, { useEffect, useState } from 'react';
import { attendanceAPI } from '../../utils/api';
import { formatDate, todayInputDate } from '../../utils/helpers';
import { adToBS, bsToAD, getDaysInBSMonth, NEPALI_MONTHS, formatNepaliDate } from '../../utils/nepaliDate';
import MemberAvatar from '../../components/MemberAvatar';
import Modal from '../../components/Modal';
import NepaliDatePicker from '../../components/NepaliDatePicker';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

function Attendance() {
  const [members, setMembers] = useState([]);
  const [shift, setShift] = useState('Morning');
  const [date, setDate] = useState(todayInputDate());
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState({});
  const [walkIns, setWalkIns] = useState([]);
  const [walkInModal, setWalkInModal] = useState(false);
  const [walkInForm, setWalkInForm] = useState({ name: '', phone: '', shift: 'Morning' });
  const [streaks, setStreaks] = useState([]);
  const [activeTab, setActiveTab] = useState('mark');
  const [search, setSearch] = useState('');

  // Member month attendance modal
  const [memberModal, setMemberModal]           = useState(false);
  const [modalMember, setModalMember]           = useState(null);
  const [modalRecords, setModalRecords]         = useState([]);
  const [modalLoading, setModalLoading]         = useState(false);
  const todayBS = adToBS(new Date());
  const [modalBsYear, setModalBsYear]   = useState(todayBS?.year  ?? 2083);
  const [modalBsMonth, setModalBsMonth] = useState(todayBS?.month ?? 0);

  const openMemberModal = async (member, bsYear, bsMonth) => {
    setModalMember(member);
    setModalBsYear(bsYear);
    setModalBsMonth(bsMonth);
    setMemberModal(true);
    setModalLoading(true);
    try {
      const firstAD = bsToAD(bsYear, bsMonth, 1);
      const lastAD  = bsToAD(bsYear, bsMonth, getDaysInBSMonth(bsYear, bsMonth));
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const res = await attendanceAPI.getMemberAttendance(member._id, { startDate: fmt(firstAD), endDate: fmt(lastAD) });
      setModalRecords(res.data);
    } catch { toast.error('Failed to load attendance'); }
    finally { setModalLoading(false); }
  };

  const fetchModalMonth = async (bsYear, bsMonth) => {
    if (!modalMember) return;
    setModalBsYear(bsYear);
    setModalBsMonth(bsMonth);
    setModalLoading(true);
    try {
      const firstAD = bsToAD(bsYear, bsMonth, 1);
      const lastAD  = bsToAD(bsYear, bsMonth, getDaysInBSMonth(bsYear, bsMonth));
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const res = await attendanceAPI.getMemberAttendance(modalMember._id, { startDate: fmt(firstAD), endDate: fmt(lastAD) });
      setModalRecords(res.data);
    } catch { toast.error('Failed to load attendance'); }
    finally { setModalLoading(false); }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await attendanceAPI.getMembersForAttendance({ shift, date });
      setMembers(res.data);
    } catch {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const fetchWalkIns = async () => {
    try {
      const res = await attendanceAPI.getWalkIns({ date });
      setWalkIns(res.data);
    } catch {}
  };

  const fetchStreaks = async () => {
    try {
      const res = await attendanceAPI.getStreaks();
      setStreaks(res.data);
    } catch {}
  };

  useEffect(() => {
    fetchMembers();
    fetchWalkIns();
  }, [shift, date]);

  useEffect(() => {
    if (activeTab === 'streaks') fetchStreaks();
  }, [activeTab]);

  const handleMark = async (memberId, memberShift) => {
    const key = `${memberId}-${memberShift}`;
    setMarking(m => ({ ...m, [key]: true }));
    try {
      await attendanceAPI.mark({ memberId, shift: memberShift, date });
      await fetchMembers();
    } catch {
      toast.error('Failed to mark attendance');
    } finally {
      setMarking(m => ({ ...m, [key]: false }));
    }
  };

  const handleWalkInSubmit = async e => {
    e.preventDefault();
    try {
      await attendanceAPI.addWalkIn({ ...walkInForm, date });
      toast.success('Walk-in logged');
      setWalkInModal(false);
      setWalkInForm({ name: '', phone: '', shift: 'Morning' });
      fetchWalkIns();
    } catch {
      toast.error('Failed to log walk-in');
    }
  };

  const getChecked = (m) => {
    if (shift === 'Morning') return m.morningCheckedIn;
    if (shift === 'Evening') return m.eveningCheckedIn;
    return false;
  };

  const checkedCount = members.filter(m => getChecked(m)).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Attendance</h2>
          <p className="text-gray-500 text-sm">{checkedCount} of {members.length} members checked in</p>
        </div>
        <button onClick={() => setWalkInModal(true)} className="btn-secondary text-sm">
          + Log Walk-in
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {['mark', 'walkins', 'streaks'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${activeTab === tab ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab === 'mark' ? 'Mark Attendance' : tab === 'walkins' ? 'Walk-ins' : 'Absent Streaks'}
          </button>
        ))}
      </div>

      {activeTab === 'mark' && (
        <>
          {/* Controls */}
          <div className="card py-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Date:</label>
                <NepaliDatePicker
                  value={date}
                  onChange={setDate}
                  maxDate={todayInputDate()}
                />
              </div>
              <div className="flex gap-1">
                {['Morning', 'Evening'].map(s => (
                  <button
                    key={s}
                    onClick={() => setShift(s)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${shift === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {s === 'Morning' ? '🌅' : '🌆'} {s}
                  </button>
                ))}
              </div>
              {/* Search */}
              <div className="relative ml-auto">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                <input
                  className="input pl-8 w-48 text-sm"
                  placeholder="Search member..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                  >✕</button>
                )}
              </div>
            </div>
          </div>

          {/* Member Table */}
          {loading ? <LoadingSpinner /> : (
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 w-8">#</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Member</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">ID</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Shift</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const q = search.toLowerCase();
                    const filtered = members.filter(m =>
                      m.name.toLowerCase().includes(q) || m.memberId.toLowerCase().includes(q)
                    );
                    if (filtered.length === 0) return (
                      <tr><td colSpan={6} className="text-center py-12 text-gray-400">
                        {search ? `No members matching "${search}"` : `No members assigned to ${shift} shift`}
                      </td></tr>
                    );
                    return filtered.map((member, i) => {
                    const checked = getChecked(member);
                    const key = `${member._id}-${shift}`;
                    const isMarking = marking[key];
                    return (
                      <tr
                        key={member._id}
                        onClick={() => openMemberModal(member, todayBS?.year ?? 2083, todayBS?.month ?? 0)}
                        className={`border-b border-gray-50 cursor-pointer transition-colors ${checked ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'}`}
                      >
                        <td className="px-4 py-3 text-gray-400 font-medium">{i + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <MemberAvatar photo={member.photo} name={member.name} size="sm" />
                            <span className="font-medium text-gray-800">{member.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{member.memberId}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${shift === 'Morning' ? 'bg-yellow-100 text-yellow-700' : 'bg-indigo-100 text-indigo-700'}`}>
                            {shift === 'Morning' ? '🌅' : '🌆'} {shift}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${checked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {checked ? '✓ Present' : '— Absent'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={e => { e.stopPropagation(); if (!isMarking) handleMark(member._id, shift); }}
                            disabled={isMarking}
                            className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto transition-all ${checked ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-gray-200 text-gray-400 hover:bg-blue-100 hover:text-blue-600'}`}
                          >
                            {isMarking
                              ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              : checked ? '✓' : '○'}
                          </button>
                        </td>
                      </tr>
                    );
                  });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'walkins' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700">Walk-ins on {formatNepaliDate(new Date(date + 'T00:00:00'))}</h3>
            <NepaliDatePicker value={date} onChange={setDate} maxDate={todayInputDate()} />
          </div>
          {walkIns.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">No walk-ins recorded for this date</p>
          ) : (
            <div className="space-y-2">
              {walkIns.map(w => (
                <div key={w._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{w.name}</p>
                    <p className="text-xs text-gray-500">{w.phone}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${w.shift === 'Morning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                    {w.shift}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'streaks' && (
        <div className="card">
          <h3 className="font-semibold text-gray-700 mb-4">Members Absent 2+ Consecutive Days</h3>
          {streaks.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-8">No members with long absent streaks</p>
          ) : (
            <div className="space-y-2">
              {streaks.map(({ member, absentDays }) => (
                <div key={member._id} className="flex items-center gap-4 p-3 bg-red-50 rounded-lg">
                  <MemberAvatar photo={member.photo} name={member.name} size="sm" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{member.name}</p>
                    <p className="text-xs text-gray-500">{member.phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-red-600">{absentDays} days</p>
                    <p className="text-xs text-gray-400">consecutive absences</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Member Monthly Attendance Modal ── */}
      <Modal isOpen={memberModal} onClose={() => setMemberModal(false)} title="Monthly Attendance" size="md">
        {modalMember && (
          <div className="space-y-4">
            {/* Member info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <MemberAvatar photo={modalMember.photo} name={modalMember.name} size="sm" />
              <div>
                <p className="font-semibold text-gray-800">{modalMember.name}</p>
                <p className="text-xs text-gray-500">{modalMember.memberId}</p>
              </div>
            </div>

            {/* Month navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  const newMonth = modalBsMonth === 0 ? 11 : modalBsMonth - 1;
                  const newYear  = modalBsMonth === 0 ? modalBsYear - 1 : modalBsYear;
                  fetchModalMonth(newYear, newMonth);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg"
              >‹</button>
              <span className="font-semibold text-gray-800">
                {NEPALI_MONTHS[modalBsMonth]} {modalBsYear} B.S.
              </span>
              <button
                onClick={() => {
                  const newMonth = modalBsMonth === 11 ? 0 : modalBsMonth + 1;
                  const newYear  = modalBsMonth === 11 ? modalBsYear + 1 : modalBsYear;
                  fetchModalMonth(newYear, newMonth);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 font-bold text-lg"
              >›</button>
            </div>

            {/* Calendar grid */}
            {modalLoading ? <LoadingSpinner /> : (() => {
              const daysInMonth = getDaysInBSMonth(modalBsYear, modalBsMonth);
              const firstAD     = bsToAD(modalBsYear, modalBsMonth, 1);
              const firstDow    = firstAD.getDay();

              // Build a set of "present" date strings (AD)
              const presentDates = new Set();
              const morningDates = new Set();
              const eveningDates = new Set();
              modalRecords.forEach(r => {
                if (r.checkedIn) {
                  presentDates.add(r.date);
                  if (r.shift === 'Morning') morningDates.add(r.date);
                  if (r.shift === 'Evening') eveningDates.add(r.date);
                }
              });

              const todayStr2 = todayInputDate();
              const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const joinStr = modalMember.joiningDate ? fmt(new Date(modalMember.joiningDate)) : null;
              let presentCount = 0;

              const cells = [];
              for (let i = 0; i < firstDow; i++) cells.push(<div key={`b${i}`} />);
              for (let day = 1; day <= daysInMonth; day++) {
                const adDate = bsToAD(modalBsYear, modalBsMonth, day);
                const adStr      = fmt(adDate);
                const isFuture   = adStr > todayStr2;
                const beforeJoin = joinStr && adStr < joinStr;
                const morning    = morningDates.has(adStr);
                const evening    = eveningDates.has(adStr);
                const present    = morning || evening;
                if (present) presentCount++;

                cells.push(
                  <div key={day} className="flex flex-col items-center gap-0.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold
                      ${beforeJoin || isFuture ? 'text-gray-300' :
                        present                ? 'bg-green-500 text-white' :
                                                 'bg-red-100 text-red-500'}`}>
                      {day}
                    </div>
                    {!isFuture && !beforeJoin && (morning || evening) && (
                      <div className="flex gap-0.5">
                        {morning && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" title="Morning" />}
                        {evening && <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" title="Evening" />}
                      </div>
                    )}
                  </div>
                );
              }

              const totalPast = Array.from({ length: daysInMonth }, (_, i) => {
                const adDate = bsToAD(modalBsYear, modalBsMonth, i + 1);
                const adStr2 = fmt(adDate);
                return adStr2 <= todayStr2 && (!joinStr || adStr2 >= joinStr);
              }).filter(Boolean).length;

              return (
                <>
                  {/* Week headers */}
                  <div className="grid grid-cols-7 mb-1">
                    {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                      <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-y-2">{cells}</div>
                  {/* Summary */}
                  <div className="flex justify-between items-center pt-3 border-t border-gray-100 text-sm">
                    <div className="flex gap-3">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Present</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-100 inline-block" /> Absent</span>
                    </div>
                    <span className="font-bold text-green-600">{presentCount} / {totalPast} days</span>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* Walk-in Modal */}
      <Modal isOpen={walkInModal} onClose={() => setWalkInModal(false)} title="Log Walk-in / Guest">
        <form onSubmit={handleWalkInSubmit} className="space-y-4">
          <div>
            <label className="label">Name *</label>
            <input className="input" value={walkInForm.name} onChange={e => setWalkInForm(f => ({ ...f, name: e.target.value }))} placeholder="Guest name" required />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={walkInForm.phone} onChange={e => setWalkInForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone number" />
          </div>
          <div>
            <label className="label">Shift</label>
            <select className="input" value={walkInForm.shift} onChange={e => setWalkInForm(f => ({ ...f, shift: e.target.value }))}>
              <option>Morning</option>
              <option>Evening</option>
            </select>
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setWalkInModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Log Walk-in</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Attendance;
